import React, { useMemo } from 'react';
import { useQuery, useMutation, useApolloClient, ApolloProvider } from '@apollo/client/react';

export { ApolloProvider };

// ApolloConsumer render-prop component (removed in Apollo Client v4)
export function ApolloConsumer({ children }) {
    const client = useApolloClient();
    return children(client);
}

// Deep-clone data so consumers can mutate it (Apollo 3+ freezes query results)
function cloneData(data) {
    if (!data) return {};
    try {
        return JSON.parse(JSON.stringify(data));
    } catch (e) {
        return data;
    }
}

// Render-prop Query component (removed in Apollo Client v4)
// Wraps useQuery hook, defaults data to {} during loading, deep-clones data for mutation safety
export function Query({ children, query, variables, fetchPolicy, client, pollInterval, skip, context, ...rest }) {
    const serializedVariables = JSON.stringify(variables);
    const memoizedVariables = useMemo(() => variables, [serializedVariables]);
    const options = useMemo(() => {
        const opts = { variables: memoizedVariables, fetchPolicy, pollInterval, skip, context, ...rest };
        if (client) opts.client = client;
        return opts;
    }, [memoizedVariables, fetchPolicy, pollInterval, skip, context, client, JSON.stringify(rest)]);
    const result = useQuery(query, options);
    const dataToClone = result.data !== undefined ? result.data : {};
    return children({ ...result, data: cloneData(dataToClone) });
}

// Render-prop Mutation component (removed in Apollo Client v4)
export function Mutation({
    children,
    mutation,
    variables,
    onCompleted,
    onError,
    refetchQueries,
    update,
    client,
    context,
    ...rest
}) {
    const options = { variables, onCompleted, onError, refetchQueries, update, context, ...rest };
    if (client) options.client = client;
    const [mutate, result] = useMutation(mutation, options);
    return children(mutate, result);
}

// withApollo HOC (removed in Apollo Client v4)
export function withApollo(Component) {
    function WithApollo(props) {
        const client = useApolloClient();
        return <Component {...props} client={client} />;
    }
    WithApollo.displayName = `withApollo(${Component.displayName || Component.name || 'Component'})`;
    return WithApollo;
}

export function compose(...funcs) {
    if (funcs.length === 0) return arg => arg;
    if (funcs.length === 1) return funcs[0];
    return funcs.reduce((a, b) => (...args) => a(b(...args)));
}
